import { __awaiter, __generator } from "tslib";
import { dynamicLoader } from '../runtime/bootstrap/scriptLoader';
import { addLogger, log } from '../runtime/logging/broadcastLogger';
import { LogKind } from '../runtime/logging/log.types';
import { makeMacKustoLogger, unlockLoggingQueue } from '../runtime/logging/macKustoLogger';
import { createUUID } from '../runtime/public/utils/uuid';
import { createTrustedTypesPolicy } from '../runtime/bootstrap/trustedTypesPolicy';
export var hvcTrustedTypePolicy = createTrustedTypesPolicy('@centro/hvc-loader');
function assert(condition, message) {
    if (!condition) {
        var error = new Error(message);
        log({ type: LogKind.Error, message: message, id: '508371', error: error });
        throw error;
    }
}
function getFeatureLoadingState(featureName) {
    if (window === undefined) {
        log({ type: LogKind.Info, message: 'window is undefined', id: '512105' });
        return false;
    }
    if (window.m365_admin_features === undefined) {
        log({ type: LogKind.Info, message: 'window.m365_admin_features is undefined', id: '512108' });
        return false;
    }
    if (window.m365_admin_features.initializers !== undefined &&
        window.m365_admin_features.initializers[featureName] !== undefined) {
        return true;
    }
    return false;
}
function getDbgState(featureName) {
    var _a, _b;
    return !!((_b = (_a = window.m365_admin_features) === null || _a === void 0 ? void 0 : _a.locDbg) === null || _b === void 0 ? void 0 : _b[featureName]);
}
var centroBaseUrl = undefined;
var uxVersionData = undefined;
var loaderInitialized = false;
var loggersInitialized = false;
var uxVersionDebugData = undefined;
var enableQueryParam = false;
var consumerAdapters = new Map();
var instanceVersionInfo = undefined;
var consumerPreConfig = undefined;
function normalizeCasingForUxVersionData(mixedCaseUxVersionData) {
    if (mixedCaseUxVersionData !== undefined && mixedCaseUxVersionData.hasOwnProperty('__hostApp_config')) {
        for (var hvcProp in mixedCaseUxVersionData) {
            if (!hvcProp.startsWith('__')) {
                mixedCaseUxVersionData[hvcProp.toLowerCase()] = mixedCaseUxVersionData[hvcProp];
                if (hvcProp.toLowerCase() !== hvcProp) {
                    delete mixedCaseUxVersionData[hvcProp];
                }
            }
        }
    }
    return JSON.stringify(mixedCaseUxVersionData);
}
function getUxVersionData(queryParams) {
    if (queryParams === void 0) { queryParams = ''; }
    return __awaiter(this, void 0, void 0, function () {
        var error, versionUrl;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (consumerPreConfig === undefined) {
                        error = new Error('attempting to getUxVersionData before preConfig were provided');
                        log({ type: LogKind.Error, message: error.message, error: error, id: '508373' });
                        throw error;
                    }
                    versionUrl = centroBaseUrl + "/admin/api/uxversion" + queryParams;
                    if (enableQueryParam) {
                        // If no query params need to be added lead with ? instead of &
                        if (queryParams === '') {
                            versionUrl = versionUrl + "?locale=" + consumerPreConfig.locale + "&hostingapp=" + consumerPreConfig.hostName;
                        }
                        else {
                            versionUrl = versionUrl + "&locale=" + consumerPreConfig.locale + "&hostingapp=" + consumerPreConfig.hostName;
                        }
                    }
                    return [4 /*yield*/, new Promise(function (resolve, reject) {
                            var req = new XMLHttpRequest();
                            req.open('GET', versionUrl, true);
                            if (!enableQueryParam) {
                                req.setRequestHeader('x-ms-mac-locale', consumerPreConfig.locale);
                                req.setRequestHeader('x-ms-mac-hostingapp', consumerPreConfig.hostName);
                            }
                            req.onreadystatechange = function () {
                                if (req.readyState === XMLHttpRequest.DONE) {
                                    var correlationId = req.getResponseHeader('x-ms-correlation-id');
                                    if (req.status !== 200) {
                                        var errorMsg = "Request to " + versionUrl + " failed, server responseCode: " + req.status + " body: " + req.response + " correlationId: " + correlationId;
                                        var err = new Error(errorMsg);
                                        reject(err);
                                        return;
                                    }
                                    resolve(normalizeCasingForUxVersionData(JSON.parse(req.response)));
                                }
                            };
                            req.send();
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
function getUxVersionDebugData() {
    return __awaiter(this, void 0, void 0, function () {
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (uxVersionDebugData) {
                        return [2 /*return*/];
                    }
                    _b = (_a = JSON).parse;
                    return [4 /*yield*/, getUxVersionData("?locdbg=true")];
                case 1:
                    uxVersionDebugData = _b.apply(_a, [_c.sent()]);
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Initialize the HVC loader.
 *
 * This should be called before calling any other hvc-loader functions
 */
export function initialize(params) {
    return __awaiter(this, void 0, void 0, function () {
        var baseUrl, preConfig, uxData, enableUxVersionQueryParam, appId, hostName, versionInfo, kustoEndpoint, hvcLoaderInitializeStartTime, queryParam, uxDataFromAPI, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    baseUrl = params.centroEnvironmentBaseUrl, preConfig = params.preConfig, uxData = params.centroHvcData, enableUxVersionQueryParam = params.enableUxVersionQueryParam;
                    // Note: user might not call us from TypeScript environment, so assert on all non-optional params.
                    assert(!!baseUrl, 'centroEnvironmentBaseUrl is missing');
                    assert(!!preConfig, 'preConfig is missing');
                    consumerPreConfig = preConfig;
                    if (!consumerPreConfig.appId) {
                        consumerPreConfig.appId = createUUID();
                    }
                    appId = consumerPreConfig.appId;
                    hostName = consumerPreConfig.hostName;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, , 6]);
                    if (loaderInitialized) {
                        log({
                            type: LogKind.Info,
                            message: 'initialize() is called but loader is already previously initialized',
                            id: '508375',
                        });
                        return [2 /*return*/];
                    }
                    centroBaseUrl = baseUrl.substring(baseUrl.length - 1) === '/' ? baseUrl.substring(0, baseUrl.length - 1) : baseUrl;
                    versionInfo = require('../../package.json');
                    instanceVersionInfo = versionInfo.version;
                    kustoEndpoint = centroBaseUrl + "/api/instrument/logclient";
                    if (!loggersInitialized) {
                        // do this first
                        addLogger('hvcLoaderKustoLogger', makeMacKustoLogger(appId, kustoEndpoint, hostName, versionInfo.name + '_' + versionInfo.version));
                        unlockLoggingQueue();
                        if (consumerPreConfig.additionalLoggers !== undefined) {
                            Object.keys(consumerPreConfig.additionalLoggers).forEach(function (key) {
                                addLogger(key, { logger: consumerPreConfig.additionalLoggers[key] });
                            });
                        }
                        loggersInitialized = true;
                    }
                    hvcLoaderInitializeStartTime = performance.now();
                    if (!!!uxData) return [3 /*break*/, 2];
                    log({ type: LogKind.Info, message: 'uxVersion Data is provided thru parameter', id: '508372', data: uxData });
                    uxVersionData = JSON.parse(uxData);
                    return [3 /*break*/, 4];
                case 2:
                    if (params.forHostVersion && params.hvcVersion) {
                        throw new Error('cannot specify both forHostVersion and hvcVersion');
                    }
                    // do not use polyfill-required API (URLSearchParams)
                    enableQueryParam = !!enableUxVersionQueryParam;
                    queryParam = '';
                    if (params.forHostVersion) {
                        queryParam = "?bldVer=" + encodeURIComponent(params.forHostVersion);
                    }
                    else if (params.hvcVersion) {
                        queryParam = "?pkgver=" + encodeURIComponent(params.hvcVersion);
                    }
                    return [4 /*yield*/, getUxVersionData(queryParam)];
                case 3:
                    uxDataFromAPI = _a.sent();
                    log({
                        type: LogKind.Info,
                        message: 'uxVersion Data is provided thru parameter',
                        id: '508372',
                        data: uxDataFromAPI,
                    });
                    uxVersionData = JSON.parse(uxDataFromAPI);
                    _a.label = 4;
                case 4:
                    loaderInitialized = true;
                    log({
                        type: LogKind.Info,
                        message: 'hvcloader initialze() step has succeeded',
                        id: '513574',
                        data: {
                            eventName: 'hvcLoader_Initialize_step',
                            fromStart: performance.now() - hvcLoaderInitializeStartTime,
                            UA: navigator.userAgent,
                            enableQueryParam: enableQueryParam,
                        },
                    });
                    return [3 /*break*/, 6];
                case 5:
                    error_1 = _a.sent();
                    log({ type: LogKind.Error, message: 'hvcloader initialze() step has failed ' + error_1.message, id: '508376', error: error_1 });
                    throw error_1;
                case 6: return [2 /*return*/];
            }
        });
    });
}
/**
 * Preload HVC to prepare for its bootstrapping.
 *
 * This should be called before attempting to bootstrap a HVC
 *
 * To preload multiple features in parallel: Promise.all([preloadFeature(), preloadFeature(), ...])
 *
 * The returning Promise resolves to [boolean, Record<string, any> | undefined], indicating if the preload has happened, with or without static additional objects
 * If the HVC is not enabled for your app in the Centro versioning server the promise resolves to false.
 */
export function preloadFeature(params) {
    return __awaiter(this, void 0, void 0, function () {
        var uxData, preloadFeatureStartTime, lowerCaseFeatureName, staticAdditionalObjects, foreignEnvMainVersion, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    uxData = uxVersionData;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 7, , 8]);
                    if (!loaderInitialized) {
                        throw new Error('initialize() needs to be invoked before calling preloadFeature()');
                    }
                    preloadFeatureStartTime = performance.now();
                    if (!params.localMode) return [3 /*break*/, 3];
                    return [4 /*yield*/, getUxVersionDebugData()];
                case 2:
                    _a.sent();
                    uxData = uxVersionDebugData;
                    _a.label = 3;
                case 3:
                    if (!uxData) {
                        throw new Error('uxVersionData is not populated');
                    }
                    lowerCaseFeatureName = params.featureName.toLowerCase();
                    if (!uxData[lowerCaseFeatureName]) {
                        log({
                            type: LogKind.Info,
                            message: "HVC " + lowerCaseFeatureName + " is not enabled for this host",
                            id: '508419',
                        });
                        return [2 /*return*/, [false, undefined]];
                    }
                    if (!(getFeatureLoadingState(lowerCaseFeatureName) && getDbgState(lowerCaseFeatureName) === !!params.localMode)) return [3 /*break*/, 4];
                    log({
                        type: LogKind.Info,
                        message: "preloadFeature() feature " + lowerCaseFeatureName + " already loaded previously",
                        id: '508465',
                        data: { hvc: lowerCaseFeatureName },
                    });
                    return [3 /*break*/, 6];
                case 4: return [4 /*yield*/, dynamicLoader(uxData[lowerCaseFeatureName].loaderSpec, hvcTrustedTypePolicy)];
                case 5:
                    _a.sent();
                    if (!getFeatureLoadingState(lowerCaseFeatureName)) {
                        throw new Error("preloadFeature(): global is not populated after loading for " + lowerCaseFeatureName);
                    }
                    if (window.m365_admin_features.locDbg === undefined) {
                        window.m365_admin_features.locDbg = {};
                    }
                    window.m365_admin_features.locDbg[lowerCaseFeatureName] = !!params.localMode;
                    _a.label = 6;
                case 6:
                    if (params.adapters) {
                        // store adapters
                        consumerAdapters.set(lowerCaseFeatureName, params.adapters);
                    }
                    staticAdditionalObjects = void 0;
                    // to get more detailed info of which hvc feature causes trouble, adding this extra log
                    try {
                        staticAdditionalObjects = window.m365_admin_features.staticAdditionalObjects[lowerCaseFeatureName];
                    }
                    catch (error) {
                        foreignEnvMainVersion = window.m365_admin_features.foreignEnvMainVersion;
                        log({
                            type: LogKind.Error,
                            message: foreignEnvMainVersion
                                ? "using v3 Hvc " + lowerCaseFeatureName + ", error when trying to retrieve staticAdditionalObjects, error: " + error.message
                                : "using v2 Hvc " + lowerCaseFeatureName + ", staticAdditionalObjects is not supported, error: " + error.message,
                            id: '521848',
                            error: error,
                        });
                        throw error;
                    }
                    log({
                        type: LogKind.Info,
                        message: 'preloadFeatures() step has succeeded',
                        id: '513585',
                        data: {
                            hvc: lowerCaseFeatureName,
                            uxdata: JSON.stringify(uxData),
                            eventName: 'preloadFeature_step',
                            fromStart: performance.now() - preloadFeatureStartTime,
                            UA: navigator.userAgent,
                        },
                    });
                    return [2 /*return*/, [true, staticAdditionalObjects]];
                case 7:
                    error_2 = _a.sent();
                    log({
                        type: LogKind.Error,
                        message: 'error in preloadFeatures() step ' + error_2.message,
                        id: '508417',
                        data: { uxdata: JSON.stringify(uxData) },
                        error: error_2,
                    });
                    throw error_2;
                case 8: return [2 /*return*/];
            }
        });
    });
}
/**
 * Bootstrap the HVC.
 *
 * This will return a function which you could use to bootstrap the HVC, the return result
 * of the function can be used to unmount the HVC
 */
export function bootstrapFeature(params) {
    return __awaiter(this, void 0, void 0, function () {
        var bootStrapFeatureStartTime, uxData, lowerCaseFeatureName, loaderData, bootstrapper, error_3, foreignEnvMainVersion, error_4;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 7, , 8]);
                    if (!loaderInitialized) {
                        throw new Error('initialize() needs to be invoked before calling bootstrapFeature()');
                    }
                    bootStrapFeatureStartTime = performance.now();
                    uxData = uxVersionData;
                    lowerCaseFeatureName = params.featureName.toLowerCase();
                    if (getDbgState(lowerCaseFeatureName)) {
                        uxData = uxVersionDebugData;
                    }
                    if (!uxData) {
                        throw new Error('uxVersionData is not populated');
                    }
                    if (!getFeatureLoadingState(lowerCaseFeatureName)) return [3 /*break*/, 5];
                    loaderData = {
                        centroBaseUrl: centroBaseUrl,
                        cdnUrl: uxData[lowerCaseFeatureName].cdnUrl,
                        ariaLoggerId: uxData.__hostApp_config.ariaLoggerId,
                        targetAppUrls: uxData.__hostApp_config.hostingAppUrls,
                        versionInfo: instanceVersionInfo,
                    };
                    bootstrapper = void 0;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, window.m365_admin_features.initializers[lowerCaseFeatureName](consumerAdapters.get(lowerCaseFeatureName), loaderData, consumerPreConfig)];
                case 2:
                    bootstrapper = _a.sent();
                    log({
                        type: LogKind.Info,
                        message: 'bootstrapFeature() step has succeeded',
                        id: '513586',
                        data: {
                            hvc: lowerCaseFeatureName,
                            eventName: 'bootstrapFeature_step',
                            fromStart: performance.now() - bootStrapFeatureStartTime,
                            UA: navigator.userAgent,
                        },
                    });
                    return [3 /*break*/, 4];
                case 3:
                    error_3 = _a.sent();
                    foreignEnvMainVersion = window.m365_admin_features.foreignEnvMainVersion;
                    log({
                        type: LogKind.Error,
                        message: foreignEnvMainVersion
                            ? "using v3 Hvc " + lowerCaseFeatureName + ", error when running foreignFeatureInitializeRuntime, error: " + error_3.message
                            : "using v2 Hvc " + lowerCaseFeatureName + " is not supported, error: " + error_3.message,
                        id: '521851',
                        error: error_3,
                    });
                    throw error_3;
                case 4: return [2 /*return*/, bootstrapper];
                case 5: throw new Error("bootstrapFeature(): preloadFeature() was not called before bootstraping " + lowerCaseFeatureName);
                case 6: return [3 /*break*/, 8];
                case 7:
                    error_4 = _a.sent();
                    log({
                        type: LogKind.Error,
                        message: 'error in bootstrapFeature() step ' + error_4.message,
                        id: '508481',
                        error: error_4,
                    });
                    throw error_4;
                case 8: return [2 /*return*/];
            }
        });
    });
}
